import React from 'react'
import ReactDOM from 'react-dom'
import ReactDomClient from 'react-dom/client'

/**
 * 兼容 React 17 和 React 18 的渲染方法
 * @param {React.ReactElement} component - 要渲染的组件
 * @param {HTMLElement} container - 渲染的容器元素
 * @returns {Object} 返回包含 unmount 和 update 方法的对象
 *
 * @example
 * const instance = renderCompat(<MyComponent />, document.getElementById('root'))
 * // 更新组件
 * instance.update(<MyComponent newProp="value" />)
 * // 卸载组件
 * instance.unmount()
 */
export const renderCompat = (component, container) => {
  let root = null

  const instance = {
    unmount: null,
    update: null,
    getRoot: null
  }

  // React 18 (createRoot API)
  if (ReactDomClient?.createRoot) {
    root = ReactDomClient.createRoot(container)

    instance.unmount = () => {
      root?.unmount()
      root = null
    }
    instance.update = (newComponent) => {
      root?.render(newComponent)
    }
    instance.getRoot = () => root

    // 克隆组件并注入方法
    const componentWithProps = React.cloneElement(component, {
      ...component.props,
      unmount: instance.unmount,
      update: instance.update,
      getRoot: instance.getRoot
    })

    root.render(componentWithProps)

    return instance
  }

  // React 17 (legacy render API)
  if (ReactDOM?.render) {
    instance.unmount = () => {
      ReactDOM?.unmountComponentAtNode(container)
    }
    instance.update = (newComponent) => {
      ReactDOM?.render(newComponent, container)
    }
    instance.getRoot = () => null

    // 克隆组件并注入方法
    const componentWithProps = React.cloneElement(component, {
      ...component.props,
      unmount: instance.unmount,
      update: instance.update,
      getRoot: instance.getRoot
    })

    ReactDOM?.render(componentWithProps, container)

    return instance
  }

  // 如果两个 API 都不可用，抛出错误
  throw new Error('无法找到可用的 React 渲染方法')
}

/**
 * 创建一个 Promise 包装的渲染方法
 * @param {React.ReactElement} component - 要渲染的组件
 * @param {HTMLElement} container - 渲染的容器元素（可选）
 * @returns {Promise<Object>} 返回 Promise，resolve 时包含渲染实例
 *
 * @example
 * renderCompatPromise(<MyModal />, document.body).then(instance => {
 *   // 组件已渲染
 * })
 */
export const renderCompatPromise = (component, container) => {
  const div = container || document.createElement('div')

  return new Promise((resolve) => {
    const instance = renderCompat(component, div)
    resolve(instance)
  })
}

/**
 * 渲染组件并自动添加到 body
 * @param {React.ReactElement} component - 要渲染的组件
 * @returns {Object} 返回包含 unmount、update 和 container 的对象
 *
 * @example
 * const { unmount, container } = renderToBody(<MyModal />)
 * // 卸载并移除容器
 * unmount()
 */
export const renderToBody = (component) => {
  const div = document.createElement('div')
  document.body.appendChild(div)

  const instance = renderCompat(component, div)

  return {
    ...instance,
    unmount: () => {
      instance.unmount()
      if (div.parentNode) {
        div.parentNode.removeChild(div)
      }
    },
    container: div
  }
}

export default renderCompat
